import React, {useState, useEffect} from 'react';
import Input from '../../../../../Assets/Input';
import Dropdown from '../../../../../Assets/Dropdown';
import { mlbOffStats, mlbDefStats, mlbOffStatVerbs, mlbDefStatVerbs } from '../../../../../../utils/mlbUtils';
import { nflStats, nflStatVerbs } from '../../../../../../utils/nflUtils';
import { nbaStats, nbaStatVerbs } from '../../../../../../utils/nbaUtils';
import { PencilIcon, PlusIcon } from '@heroicons/react/24/outline';

import StatComponent from '../StatComponent';

// utils
import { handleNumberInput } from '../../../../../../utils/formUtils';

const StatAverages = (props) => {
    const [statDisp, setStatDisp] = useState(null);
    const [sport, setSport] = useState("");
    const [compType, setCompType] = useState("");
    const [statObj, setStatObj] = useState({
        team: "",
        type: "",
        stat: "",
        label: ""
    });
    const [statObj2, setStatObj2] = useState({
        team: "",
        type: "",
        stat: "",
        label: ""
    });
    const [rangeType, setRangeType] = useState("");
    const [min, setMin] = useState("");
    const [max, setMax] = useState("");
    const [initCrit, setInitCrit] = useState(null);
    const [critIdx, setCritIdx] = useState(null);
    const [errs, setErrs] = useState([]);
    const [errMsg, setErrMsg] = useState(null);
    const stats = {
        mlb: {
            off: mlbOffStats,
            def: mlbDefStats
        },
        nfl: nflStats,
        nba: nbaStats
    };
    const statVerbs = {
        mlb: {
            off: mlbOffStatVerbs,
            def: mlbDefStatVerbs
        },
        nfl: nflStatVerbs,
        nba: nbaStatVerbs
    };

    const comparisonOptions = [
        { value: "sum", label: "Combine two stats" },
        { value: "diff", label: "Compare two stats" }
    ];

    const filterOptions = [
        { value: "between", label: "between" },
        { value: "greater", label: "greater than" },
        { value: "less", label: "less than" }
    ];

    const formFields = {
        min: (
            <div className="relative">
                <Input
                    id="min"
                    type="number"
                    value={min}
                    onChange={(e) => handleMinChange(e.target.value)}
                    className={errs.includes('min') ? 'ring-2 ring-brand-red-500 border-brand-red-500' : ''}
                    placeholder=" "
                    aria-label="Minimum Value"
                />
                <label
                    htmlFor="min"
                    className={`absolute -top-2 left-2 inline-block bg-white dark:bg-gray-900 px-1 text-xs font-medium ${
                        errs.includes('min') ? 'text-brand-red-500' : 'text-gray-900 dark:text-gray-400'
                    }`}
                >
                    Minimum Value
                </label>
            </div>
        ),
        max: (
            <div className="relative">
                <Input
                    id="max"
                    type="number"
                    value={max}
                    onChange={(e) => handleMaxChange(e.target.value)}
                    className={errs.includes('max') ? 'ring-2 ring-brand-red-500 border-brand-red-500' : ''}
                    placeholder=" "
                    aria-label="Maximum Value"
                />
                <label
                    htmlFor="max"
                    className={`absolute -top-2 left-2 inline-block bg-white dark:bg-gray-900 px-1 text-xs font-medium ${
                        errs.includes('max') ? 'text-brand-red-500' : 'text-gray-900 dark:text-gray-400'
                    }`}
                >
                    Maximum Value
                </label>
            </div>
        )
    };

    const forms = {
        "between": (
            <div className="space-y-4">
                {formFields.min}
                {formFields.max}
            </div>
        ),
        "greater": formFields.min,
        "less": formFields.max    
    };
    const compLabels = {
        "sum": "sum of",
        "diff" : "difference between"
    };

    // run when sys context changes - set sport
    useEffect(()=>{
        setSport(props.sysContext.sport);
    },[props.sysContext]);

    // run when init crit changes - set fields if crit was passed to component
    useEffect(()=> {
        // check if user is editing an existing criteria
        if(props.initCrit){
            setCritIdx(props.initCrit.index);
            const critCopy = {...props.initCrit.crit};
            setRangeType(critCopy.rangeType);
            setStatObj(critCopy.statObj);
            setInitCrit(critCopy);
            if(critCopy.type === "stat.avg-comp"){
                setStatObj2(critCopy.statObj2);
                setCompType(critCopy.compType);
            }; 
            switch(critCopy.rangeType){
                case "between":
                    setMin(critCopy.min);
                    setMax(critCopy.max);
                    break;
                case "greater":
                    setMin(critCopy.min);
                    break;
                case "less":
                    setMax(critCopy.max);
                    break;
                default: 
                    break;
            };
        };
    },[props.initCrit]);
    
    // function to check if init criteria has changed
    const checkCritChange = () => {
        // function to check a stat object for changes against the initial
        const isStatObjModified = (initObj, obj) => {
            if(initObj.type !== obj.type || initObj.team !== obj.team || initObj.team !== obj.team || initObj.label != obj.label){
                return true;
            }else return false; 
        };
        // if criteria index - check for changes, otherwise return true
        if(critIdx  !== null){
            // check stat objects for changes
            const statObjModified = isStatObjModified(initCrit.statObj, statObj);
            const statObj2Modified = props.type === "avg-comp" ? isStatObjModified(initCrit.statObj2, statObj2) : null;
            const compTypeChanged = (props.type === "avg-comp") && (initCrit.compType !== compType) ? true : false; 
            if(statObjModified || statObj2Modified || rangeType !== initCrit.rangeType || compTypeChanged){
                return true;
            };
            // check min or max values for changes
            switch(rangeType){
                case "between":
                    if(parseFloat(min) !== initCrit.min || parseFloat(max) !== initCrit.max){
                        return true;
                    };
                    break;
                case "greater":
                    if(parseFloat(min) !== initCrit.min){
                        return true;
                    };
                    break;
                case "less":
                    if(parseInt(max, 10) !== initCrit.max){
                        return true;
                    };
                    break;
            };
        }else{
            return true;
        };
    };

    // function to check for errors
    const checkErrs = () => {
        let tmpErrs = [];
        let tmpErrMsg = null;

        const checkStatObjErrs = (statNum, obj) => {
            // team validation 
            if((obj.team !== "home" && obj.team !== "away") || !(obj.type in stats[sport]) || !(obj.stat in stats[sport][obj.type]) || obj.label === ""){
                tmpErrs.push(`stat${statNum}-div`);
            };
        };

        // check for errs in stat object (always)
        checkStatObjErrs(1, statObj);

        // if comparing two stats - check second stat object and comparison type 
        if(props.type === "avg-comp"){ 
            checkStatObjErrs(2, statObj2);
            if(compType !== "sum" && compType !== "diff") tmpErrs.push("compType");
        };
        
        // Range type & min, max validation
        switch(rangeType){
            case "between":
                if(isNaN(min) || min === ""){
                    tmpErrs.push("min");
                };
                if(isNaN(max) || max === ""){
                    tmpErrs.push("max");
                };
                if(!isNaN(min) && min !== "" && !isNaN(max) && max !== ""){
                    if(parseFloat(min) >= parseFloat(max)){
                        tmpErrs.push("min");
                        tmpErrs.push("max");
                        tmpErrMsg = "Please ensure the minimum odds value is less than the maximum";
                    };
                };
                break;
            case "greater":
                if(isNaN(min) || min === ""){
                    tmpErrs.push("min");
                };
                break;
            case "less":
                if(isNaN(max) || max === ""){
                    tmpErrs.push("max");
                };
                break;
            default:
                tmpErrs.push("rangeType");
                break;
        };

        return({errs: tmpErrs, msg: tmpErrMsg});
    };

    const getLabel = () => {
        let label = "";
        if(props.type === "avg"){
            const statLbl = statObj.label[0].toUpperCase() + statObj.label.slice(1);
            const statVerb = statVerbs[sport][statObj.type][statObj.stat];            
            label = `${statLbl} ${statVerb} ${rangeType} `;
        }else if(props.type === "avg-comp"){
            label = `${compLabels[compType]} ${statObj.label} and ${statObj2.label} is ${rangeType} `;
        };
        switch(rangeType){
            case "between":
                label = label + `${min} and ${max}`
                break;
            case "greater":
                label = `${label}than ${min}`
                break;
            case "less":
                label = `${label}than ${max}`
                break;
        };
        return label;
    };

    // function to get criteria for submission 
    const getCriteria = () => {
        const label = getLabel();
        let crit = {
            type: `stat.${props.type}`,
            rangeType: rangeType,
            statObj: statObj,
            label: label
        };
        if(props.type === "avg-comp"){
            crit.statObj2 = statObj2;
            crit.compType = compType;
        };
        switch(rangeType){
            case "between":
                crit.min = parseFloat(min);
                crit.max = parseFloat(max);
                break;
            case "greater":
                crit.min = parseFloat(min);
                break;
            case "less":
                crit.max = parseFloat(max);
                break;
        };

        return crit;
    };

    // set submit function in parent component
    const validateSubmit = (e) => {
        e.preventDefault();
        // check for errors
        const errCheck = checkErrs(); 
        setErrMsg(errCheck.msg);
        setErrs(errCheck.errs);
        if(errCheck.errs.length === 0){
            // get criteria
            const crit = getCriteria();
            // check for crit change - returns true if initCrit was modified or not passed
            const modified = checkCritChange();
            if(modified){
                props.sysContext.changeCrit(crit, critIdx);
            };
            props.sysContext.setCritDisp(null);
            props.sysContext.setStep(2);
        };
    };

    // run whenever errs changes
    useEffect(()=>{
        // get all elements previously in error state and remove class
        let prevErrs = document.getElementsByClassName("err");
        while(prevErrs.length){
            prevErrs[0].classList.remove("err");
        };
        // add err class based on updated errs
        errs.forEach(err=>{
            const elem = document.getElementById(err);
            if(elem){
                elem.classList.add("err");
            };
        });
    },[errs]);

    const dispCrit = () => {
        setStatDisp(null);
    };

    // Helper function to clear specific errors
    const clearError = (errorType) => {
        if (errs.includes(errorType)) {
            setErrs(prev => prev.filter(err => err !== errorType));
            if (errMsg && (errorType === 'min' || errorType === 'max')) {
                setErrMsg(null);
            }
        }
    };

    // Update compType handler with error clearing
    const handleCompTypeChange = (value) => {
        setCompType(value);
        if (value && (value === 'sum' || value === 'diff')) {
            clearError('compType');
        }
    };

    // Update rangeType handler with error clearing
    const handleRangeTypeChange = (value) => {
        setRangeType(value);
        if (value && ['between', 'greater', 'less'].includes(value)) {
            clearError('rangeType');
        }
    };

    // Update min handler with error clearing
    const handleMinChange = (value) => {
        handleNumberInput(value, setMin);
        if (value !== '') {
            clearError('min');
            // If we have both min and max values, check if we can clear the range error
            if (max !== '' && parseFloat(value) < parseFloat(max)) {
                setErrMsg(null);
            }
        }
    };

    // Update max handler with error clearing
    const handleMaxChange = (value) => {
        handleNumberInput(value, setMax);
        if (value !== '') {
            clearError('max');
            // If we have both min and max values, check if we can clear the range error
            if (min !== '' && parseFloat(min) < parseFloat(value)) {
                setErrMsg(null);
            }
        }
    };

    // Update stat object handlers with error clearing
    useEffect(() => {
        if (statObj.team && statObj.type && statObj.stat && statObj.label) {
            clearError('stat1-div');
        }
    }, [statObj]);

    useEffect(() => {
        if (props.type === 'avg-comp' && statObj2.team && statObj2.type && statObj2.stat && statObj2.label) {
            clearError('stat2-div');
        }
    }, [statObj2]);

    return (
        <>
            {statDisp === null ? (
                <div className="space-y-6">
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-5">
                        <h2 className="text-xl font-semibold leading-6 text-gray-900 dark:text-white">
                            Stat Average Criteria
                        </h2>
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                            Set your statistical average criteria for filtering bets.
                        </p>
                    </div>

                    <form className="space-y-6">
                        {(props.type === "avg-comp" && sport !== "") && (
                            <div className="space-y-4">
                                <div>
                                    <Dropdown
                                        id="comp-type"
                                        title="Choose Stat Comparison"
                                        items={comparisonOptions}
                                        onSelect={handleCompTypeChange}
                                        activeItem={compType}
                                        className={errs.includes('compType') ? 'ring-2 ring-brand-red-500' : ''}
                                    />
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div id="stat1-div" className={`rounded-lg border ${errs.includes('stat1-div') ? 'border-brand-red-500' : 'border-gray-300 dark:border-gray-700'} p-4`}>
                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">First Stat</p>
                                        {statObj.label === "" ? (
                                            <button
                                                type="button"
                                                onClick={() => setStatDisp(1)}
                                                className="w-full flex items-center justify-center p-2 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-md hover:border-gray-400 dark:hover:border-gray-600"
                                            >
                                                <PlusIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                                            </button>
                                        ) : (
                                            <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-md p-2">
                                                <p className="text-sm text-gray-900 dark:text-gray-100">{statObj.label}</p>
                                                <button type="button" onClick={() => setStatDisp(1)}>
                                                    <PencilIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div id="stat2-div" className={`rounded-lg border ${errs.includes('stat2-div') ? 'border-brand-red-500' : 'border-gray-300 dark:border-gray-700'} p-4`}>
                                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Second Stat</p>
                                        {statObj2.label === "" ? (
                                            <button
                                                type="button"
                                                onClick={() => setStatDisp(2)}
                                                className="w-full flex items-center justify-center p-2 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-md hover:border-gray-400 dark:hover:border-gray-600"
                                            >
                                                <PlusIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                                            </button>
                                        ) : (
                                            <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-md p-2">
                                                <p className="text-sm text-gray-900 dark:text-gray-100">{statObj2.label}</p>
                                                <button type="button" onClick={() => setStatDisp(2)}>
                                                    <PencilIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {(props.type === "avg" && sport !== "") && (
                            <div id="stat1-div" className={`rounded-lg border ${errs.includes('stat1-div') ? 'border-brand-red-500' : 'border-gray-300 dark:border-gray-700'} p-4`}>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Stat</p>
                                {statObj.label === "" ? (
                                    <button
                                        type="button"
                                        onClick={() => setStatDisp(1)}
                                        className="w-full flex items-center justify-center p-2 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-md hover:border-gray-400 dark:hover:border-gray-600"
                                    >
                                        <PlusIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                                    </button>
                                ) : (
                                    <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-md p-2">
                                        <p className="text-sm text-gray-900 dark:text-gray-100">{statObj.label}</p>
                                        <button type="button" onClick={() => setStatDisp(1)}>
                                            <PencilIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        <div>
                            <Dropdown
                                id="rangeType"
                                title="Choose Filter"
                                items={filterOptions}
                                onSelect={handleRangeTypeChange}
                                activeItem={rangeType}
                                className={errs.includes('rangeType') ? 'ring-2 ring-brand-red-500' : ''}
                            />
                        </div>

                        {rangeType !== "" && forms[rangeType]}

                        {errMsg && (
                            <p className="mt-2 text-sm text-brand-red-500 dark:text-brand-red-400">
                                {errMsg}
                            </p>
                        )}

                        <div className="flex items-center justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => props.cancel()}
                                className="rounded-md bg-white dark:bg-gray-800 px-4 py-2.5 text-sm font-semibold text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={(e) => validateSubmit(e)}
                                className="rounded-md bg-brand-red-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red-500"
                            >
                                Save Criteria
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                <>
                    {statDisp === 1 && (
                        <StatComponent sport={sport} statObj={statObj} setStatObj={setStatObj} stats={stats[sport]} dispCrit={dispCrit}/>
                    )}
                    {statDisp === 2 && (
                        <StatComponent sport={sport} statObj={statObj2} setStatObj={setStatObj2} stats={stats[sport]} dispCrit={dispCrit}/>
                    )}
                </>
            )}
        </>
    );
};

export default StatAverages;